import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Package,
  Paintbrush,
  Download,
  Loader2,
  Trash2,
  Plus,
  FileSpreadsheet,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { socialPostingService } from '../../services/socialPosting';
import { ProductCard } from './ProductCard';
import { SocialCanvasManager } from './canvas/SocialCanvasManager';
import { SocialExporter } from './SocialExporter';
import { CSVImporter } from './CSVImporter';
import type {
  SocialPost,
  SocialPostProduct,
  SocialPostDesign,
} from '../../services/socialPosting';

type Tab = 'products' | 'design' | 'export';

interface PostDetailProps {
  post: SocialPost;
  onBack: () => void;
  onPostUpdated: (post: SocialPost) => void;
  onPostDeleted: () => void;
}

export function PostDetail({ post, onBack, onPostUpdated, onPostDeleted }: PostDetailProps) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('products');
  const [products, setProducts] = useState<SocialPostProduct[]>([]);
  const [designs, setDesigns] = useState<SocialPostDesign[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [isLoadingDesigns, setIsLoadingDesigns] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCSVImporter, setShowCSVImporter] = useState(false);

  const loadProducts = useCallback(async () => {
    try {
      setIsLoadingProducts(true);
      const data = await socialPostingService.listProducts(post.id);
      setProducts(data);
    } catch {
      // handled
    } finally {
      setIsLoadingProducts(false);
    }
  }, [post.id]);

  const loadDesigns = useCallback(async () => {
    try {
      setIsLoadingDesigns(true);
      const data = await socialPostingService.listDesigns(post.id);
      setDesigns(data);
    } catch {
      // handled
    } finally {
      setIsLoadingDesigns(false);
    }
  }, [post.id]);

  useEffect(() => {
    loadProducts();
    loadDesigns();
  }, [loadProducts, loadDesigns]);

  const handleRemoveProduct = async (productId: string) => {
    try {
      await socialPostingService.removeProduct(post.id, productId);
      setProducts((prev) => prev.filter((p) => p.id !== productId));
    } catch {
      // handled
    }
  };

  const handleDeletePost = async () => {
    if (!window.confirm('Delete this workspace and all its data?')) return;
    try {
      setIsDeleting(true);
      await socialPostingService.deletePost(post.id);
      onPostDeleted();
    } catch {
      setIsDeleting(false);
    }
  };

  const handleDesignCreated = (design: SocialPostDesign) => {
    setDesigns((prev) => [design, ...prev]);
  };

  const handleDesignUpdated = (design: SocialPostDesign) => {
    setDesigns((prev) => prev.map((d) => (d.id === design.id ? design : d)));
  };

  const handleDesignDeleted = (designId: string) => {
    setDesigns((prev) => prev.filter((d) => d.id !== designId));
  };

  const tabs: { key: Tab; label: string; icon: React.ElementType; count?: number }[] = [
    { key: 'products', label: 'Products', icon: Package, count: products.length },
    { key: 'design', label: 'Design', icon: Paintbrush, count: designs.length },
    { key: 'export', label: 'Export', icon: Download },
  ];

  const statusColor = post.status === 'active'
    ? 'bg-green-500/15 text-green-400 border-green-500/30'
    : post.status === 'archived'
      ? 'bg-gray-500/15 text-gray-400 border-gray-500/30'
      : 'bg-blue-500/15 text-blue-400 border-blue-500/30';

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150 group"
      >
        <ArrowLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform duration-150" />
        <span>Back to workspaces</span>
      </button>

      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-3xl font-bold tracking-tight">{post.name}</h1>
            <Badge variant="outline" className={statusColor}>{post.status}</Badge>
          </div>
          {post.description && (
            <p className="text-muted-foreground mt-1">{post.description}</p>
          )}
        </div>
        <Button
          variant="outline"
          className="rounded-lg text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={handleDeletePost}
          disabled={isDeleting}
        >
          {isDeleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
          Delete
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1.5 p-1 bg-muted/50 rounded-xl border">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`
                flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-200
                ${isActive
                  ? 'bg-background text-foreground shadow-sm border'
                  : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                }
              `}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
              {tab.count !== undefined && (
                <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5 text-xs rounded-full">
                  {tab.count}
                </Badge>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'products' && (
          <div className="space-y-4">
            {/* Action bar */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{products.length}</span> product{products.length !== 1 ? 's' : ''}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" className="rounded-lg" onClick={() => setShowCSVImporter(true)}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Import CSV
                </Button>
                <Button className="rounded-lg" onClick={() => navigate('/feedwatch')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add from Feed
                </Button>
              </div>
            </div>

            {isLoadingProducts ? (
              <div className="flex items-center justify-center py-24">
                <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-24">
                <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No products in this workspace yet.</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Add products from your feed or import a CSV file.
                </p>
                <div className="flex items-center justify-center gap-3 mt-4">
                  <Button variant="outline" className="rounded-lg" onClick={() => setShowCSVImporter(true)}>
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Import CSV
                  </Button>
                  <Button className="rounded-lg" onClick={() => navigate('/feedwatch')}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add from Feed
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {products.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onRemove={() => handleRemoveProduct(product.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'design' && (
          <SocialCanvasManager
            postId={post.id}
            designs={designs}
            isLoading={isLoadingDesigns}
            products={products}
            onDesignCreated={handleDesignCreated}
            onDesignUpdated={handleDesignUpdated}
            onDesignDeleted={handleDesignDeleted}
            breadcrumbSegments={[{ label: post.name, onClick: onBack }]}
          />
        )}

        {activeTab === 'export' && (
          <SocialExporter
            postId={post.id}
            designs={designs}
            products={products}
          />
        )}
      </div>

      {/* CSV Import Dialog */}
      <CSVImporter
        postId={post.id}
        open={showCSVImporter}
        onClose={() => setShowCSVImporter(false)}
        onImport={() => {
          loadProducts();
          setShowCSVImporter(false);
        }}
      />
    </div>
  );
}
